// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@awc/App/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setUserFields from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SetUserFields";
import { getCurrentEditingUser } from "@insite/client-framework/Store/Pages/UserSetup/UserSetupSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    editingUser: getCurrentEditingUser(state),
    emailErrorMessage: state.pages.userSetup.emailErrorMessage,
    firstNameErrorMessage: state.pages.userSetup.firstNameErrorMessage,
    lastNameErrorMessage: state.pages.userSetup.lastNameErrorMessage,
    useEmailAsUserName: getSettingsCollection(state).accountSettings.useEmailAsUserName,
    // XNG-Changes
    isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
});

const mapDispatchToProps = {
    setUserFields,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface UserSetupUserInformationStyles {
    title?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    userNameTextField?: TextFieldPresentationProps;
    emailAddressTextField?: TextFieldPresentationProps;
    firstNameTextField?: TextFieldPresentationProps;
    lastNameTextField?: TextFieldPresentationProps;
    // XNG-Changes
    requiredText?: TypographyPresentationProps;
}

export const userSetupUserInformationStyles: UserSetupUserInformationStyles = {
    title: { variant: "h5" },
    wrapper: {
        css: css`
            padding-bottom: 15px;
        `,
    },
};

const styles = userSetupUserInformationStyles;
// Xng-Changes
const formId = "userSetupForm";

const UserSetupUserInformation = ({
    editingUser,
    emailErrorMessage,
    firstNameErrorMessage,
    lastNameErrorMessage,
    useEmailAsUserName,
    setUserFields,
    // XNG-Changes
    isMobileScreen,
}: Props) => {
    const [email, setEmail] = useState(editingUser?.email || "");
    const [userName, setUserName] = useState(editingUser?.userName || "");

    useEffect(() => {
        setEmail(editingUser?.email || "");
    }, [editingUser?.email]);

    useEffect(() => {
        setUserName(editingUser?.userName || "");
    }, [editingUser?.userName]);

    // XNG-Changes: moving the return code after the react hooks (useState...etc) to prevent "internal react error: expected static flag was missing"
    if (!editingUser) {
        return null;
    }

    const emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUserFields({ email: event.target.value });
        setEmail(event.target.value);
        if (useEmailAsUserName) {
            setUserName(event.target.value);
        }
    };

    const emailBlurHandler = () => {
        setUserFields({ email });
    };

    const firstNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUserFields({ firstName: event.target.value });
    };

    const lastNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUserFields({ lastName: event.target.value });
    };

    return (
        <>
            <Typography as="h2" {...styles.title} data-test-selector="userSetupInfo_title">
                {translate("User Information")}
            </Typography>
            <Typography {...styles.requiredText}>{translate("Required")}</Typography>
            <StyledWrapper {...styles.wrapper}>
                <TextField
                    value={userName}
                    label={translate("Username")}
                    {...styles.userNameTextField}
                    disabled={true}
                    aria-labelledby="userName"
                    data-test-selector="userSetupInfo_userName"
                    // XNG-Change
                    type="userName"
                    labelPosition={isMobileScreen ? "top" : "left"}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                <TextField
                    value={email}
                    label={translate("Email Address")}
                    {...styles.emailAddressTextField}
                    aria-labelledby="emailAddress"
                    // XNG-Change
                    type="email"
                    required
                    onChange={emailChangeHandler}
                    onBlur={emailBlurHandler}
                    error={emailErrorMessage}
                    data-test-selector="userSetupInfo_email"
                    // Xng-Changes
                    placeholder={translate("Enter Email Address")}
                    labelPosition={isMobileScreen ? "top" : "left"}
                    {...{ formId, showError: !!emailErrorMessage, displayStarForRequired: true }}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                <TextField
                    value={editingUser.firstName}
                    label={translate("First Name")}
                    {...styles.firstNameTextField}
                    aria-labelledby="firstName"
                    // XNG-Change
                    type="firstName"
                    required
                    onChange={firstNameChangeHandler}
                    error={firstNameErrorMessage}
                    data-test-selector="userSetupInfo_firstName"
                    // Xng-Changes
                    placeholder={translate("Enter First Name")}
                    labelPosition={isMobileScreen ? "top" : "left"}
                    {...{ formId, showError: !!firstNameErrorMessage, displayStarForRequired: true }}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                <TextField
                    value={editingUser.lastName}
                    label={translate("Last Name")}
                    {...styles.lastNameTextField}
                    aria-labelledby="lastName"
                    // Xng-Changes
                    type="lastName"
                    required
                    onChange={lastNameChangeHandler}
                    error={lastNameErrorMessage}
                    data-test-selector="userSetupInfo_lastName"
                    // Xng-Changes
                    placeholder={translate("Enter Last Name")}
                    labelPosition={isMobileScreen ? "top" : "left"}
                    {...{ formId, showError: !!lastNameErrorMessage, displayStarForRequired: true }}
                />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UserSetupUserInformation),
    definition: {
        group: "User Setup",
        displayName: "User Information",
        allowedContexts: ["UserSetupPage"],
    },
};

export default widgetModule;
